// @flow
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import type { Element } from 'react';

import { envUrl } from '../../services/environment/EnvironmentService';

import './SplitButton.scss';

const lockBodyScrollingClass = 'body--locked';
const lockBodyGreyOutClass = 'body--grey-out-hack';

type DOMToggleObject = {
  selector: string,
  className: string,
  type: string,
};

type Item = {
  icon: Element<any>,
  label: string,
  link: string,
  id: string,
  onClick: () => void,
};

type Props = {
  icon: Element<any>,
  onChange: (boolean) => void,
  options: [Item],
  actions: {
    toggleDOMClass: (DOMToggleObject) => void,
  },
};

type State = {
  isActive: boolean,
};

class SplitButtonComponent extends React.Component<void, Props, State> {
  static displayName = 'SplitButton';

  static propTypes = {
    icon: PropTypes.element,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.element,
        label: PropTypes.string,
        link: PropTypes.string,
        id: PropTypes.string,
        onClick: PropTypes.func,
        gaAction: PropTypes.string,
      }),
    ).isRequired,
    actions: PropTypes.shape({
      toggleDOMClass: PropTypes.func.isRequired,
    }).isRequired,
  };

  listener: ?Event = window.addEventListener('click', (e: Event) => {
    if (this.state.isActive) {
      e.stopPropagation();
      this.setButtonState(false);
    }
  });

  constructor(props: Props): void {
    super(props);
    this.state = {
      isActive: false,
    };
  }

  componentDidMount() {
    this.isComponentMounted = true;
  }

  componentWillUnmount(): void {
    this.isComponentMounted = false;
    window.removeEventListener('click', this.listener);
  }

  toggleButtonState = (e: Event): void => {
    e.stopPropagation();
    this.setButtonState(!this.state.isActive);
  };

  toggleBodyClass(isActive: boolean, className: string): void {
    this.props.actions.toggleDOMClass({
      className,
      selector: 'body',
      type: isActive ? 'add' : 'remove',
    });
  }

  setButtonState = (isActive: boolean): void => {
    if (isActive === this.state.isActive) {
      return;
    }

    this.toggleBodyClass(isActive, lockBodyScrollingClass);
    this.toggleBodyClass(isActive, lockBodyGreyOutClass);

    if (this.isComponentMounted) {
      this.setState({ isActive });
    }

    this.props.onChange(isActive);
  };

  splitButtonOption() {
    return (item: Item) => {
      const onClick = (): void => {
        if (item.onClick) {
          item.onClick();
        }
      };

      return (
        <Link to={envUrl(item.link)} key={`split-button__link--${item.id}`}>
          <div className="split-button__option" key={`option--${item.id}`} onClick={onClick}>
            <div className="split-button__option-container">
              <span className="split-button__icon">{item.icon}</span>
              <span className="split-button__label u-fontFamily-primaryMedium">{item.label}</span>
            </div>
          </div>
        </Link>
      );
    };
  }

  render(): Element<any> {
    const { isActive } = this.state;

    return (
      <div className="split-button">
        <div className={`split-button__cover ${isActive ? 'split-button__cover--active' : ''}`} />
        <div className={`split-button ${isActive ? 'split-button--active' : ''}`}>
          <div className="split-button__container">{this.props.options.map(this.splitButtonOption())}</div>
          <div className="split-button__button" onClick={this.toggleButtonState}>
            {this.props.icon}
          </div>
        </div>
      </div>
    );
  }
}

export default SplitButtonComponent;
